<script lang="ts">
  import Table, {
    type ColumnDef,
    type DataRow,
    type TableGetRows,
  } from "@kontextwerk/web-sdk/ui/layout/Table.svelte"
  import type { TableSelectionChangeDetail } from "@kontextwerk/web-sdk/core/layout/table"
  import type { Snippet } from "svelte"
  import CancelReplanAppointmentCell from "../../../../../cancel-replan-appointment-cell"
  import CancelReplanPropertyCell from "./_modules/cancel-replan-property-cell"
  import CancelReplanWorkOrderCell from "./_modules/cancel-replan-work-order-cell"
  import WorkOrderContactTags from "../../../../../../../../../_shared/_modules/work-order-contact-tags"
  import type {
    CancelReplanRow,
    CancelReplanUnitContactContext,
  } from "../../../../../../_logic/cancel-replan-contract"
  import { createStaticRowsRefreshKey } from "../../../../../../_logic/rows"
  import WorkOrderTableCell from "../../../../../work-order-table-cell"
  import {
    STABLE_ROW_NUMBER_COLUMN_ID,
    createStableRowNumberState,
    getStableSortedRowPage,
    stableRowNumberColumn,
    stableRowNumberSort,
    withStableRowNumbers,
  } from "../../../../../../../../../_shared/_logic/stable-row-numbers"

  let {
    rows = [],
    unitContactContexts = [],
    loading = false,
    readOnly = false,
    enableSelection = false,
    batchActions = undefined,
    onResetRow = undefined,
    onRestoreRow = undefined,
    onRemoveRow,
    onSelectionChange = undefined,
  }: {
    rows?: CancelReplanRow[]
    unitContactContexts?: CancelReplanUnitContactContext[]
    loading?: boolean
    readOnly?: boolean
    enableSelection?: boolean
    batchActions?: Snippet<[TableSelectionChangeDetail]>
    onResetRow?: (rowId: string) => void
    onRestoreRow?: (rowId: string) => void
    onRemoveRow: (rowId: string) => void
    onSelectionChange?: (detail: TableSelectionChangeDetail) => void
  } = $props()

  type LoadingCancelReplanRow = {
    id: string
    isLoading: true
  }
  type CancelTargetsTableRow = CancelReplanRow | LoadingCancelReplanRow
  type RowControlCommand = {
    kind: "remove" | "restore" | "reset"
    disabled?: boolean
    onClick?: () => void | Promise<void>
  }

  const rowDisabled = (row: CancelTargetsTableRow) =>
    "isLoading" in row || Boolean("disabled" in row && row.disabled)

  const rowNumberState = createStableRowNumberState()
  const columns: ColumnDef[] = [
    stableRowNumberColumn(),
    { id: "orderSummary", label: "Auftrag" },
    { id: "propertyLabel", label: "Liegenschaft" },
    { id: "statusLabel", label: "Termin" },
    { id: "recipientLabel", label: "Ansprechpartner" },
  ]
  const loadingRows = $derived<LoadingCancelReplanRow[]>(
    Array.from({ length: rows.length ? Math.min(rows.length, 8) : 3 }, (_, index) => ({
      id: `cancel-target-loading-${index}`,
      isLoading: true,
    })),
  )
  const tableRows = $derived<CancelTargetsTableRow[]>(loading ? loadingRows : rows)
  const numberedRows = $derived(withStableRowNumbers(tableRows, rowNumberState))
  const refreshKey = $derived(
    loading ? `cancel-target-loading:${tableRows.length}` : createStaticRowsRefreshKey(rows),
  )
  const getRows: TableGetRows = (params) => {
    const { limit, offset } = params
    const sort = (params as { sort?: typeof stableRowNumberSort }).sort

    return getStableSortedRowPage({
      rows: numberedRows,
      columns,
      sort,
      limit,
      offset,
    })
  }

  const rowControlsFor = (row: DataRow): RowControlCommand[] => {
    const item = row as CancelTargetsTableRow

    if ("isLoading" in item) return []

    if (item.removed) {
      return [
        {
          kind: "restore",
          disabled: readOnly || !onRestoreRow,
          onClick: () => onRestoreRow?.(item.id),
        },
      ]
    }

    return [
      {
        kind: "reset",
        disabled: readOnly || rowDisabled(item) || !onResetRow,
        onClick: () => onResetRow?.(item.id),
      },
      {
        kind: "remove",
        disabled: readOnly || rowDisabled(item),
        onClick: () => onRemoveRow(item.id),
      },
    ]
  }
</script>

<Table
  {columns}
  {getRows}
  {refreshKey}
  defaultSort={stableRowNumberSort}
  theme="light"
  mode="table-scroll"
  pageSize={80}
  showActions={!readOnly}
  actionsHeaderLabel="Aktion"
  enableSelection={enableSelection && !readOnly}
  {batchActions}
  {onSelectionChange}
  rowControls={!readOnly ? rowControlsFor : undefined}
  getRowState={(row) => {
    const item = row as CancelTargetsTableRow

    if ("isLoading" in item) {
      return { disabled: true, selectable: false, disabledReason: "Termine werden geladen." }
    }

    const rowDisabled = readOnly || Boolean(item.disabled)

    return {
      disabled: rowDisabled,
      selectable: !rowDisabled && !item.removed,
      muted: item.removed,
      mutedReason: item.removed ? "Diese Zeile wird in diesem Schritt ausgelassen." : null,
      disabledReason: item.disabledReason ?? item.blockedReason ?? null,
    }
  }}
>
  {#snippet tableCell({ row, column })}
    {@const item = row as CancelTargetsTableRow}
    {#if "isLoading" in item}
      <div
        class:wide={column.id === "orderSummary" || column.id === "propertyLabel"}
        class="loading-cell"
        aria-label="Termine werden geladen"
      >
        <span class:compact={column.id === "statusLabel"} class="loading-placeholder"></span>
      </div>
    {:else if column.id === STABLE_ROW_NUMBER_COLUMN_ID}
      {item[STABLE_ROW_NUMBER_COLUMN_ID]}
    {:else if item.workOrderRow && ["orderSummary", "statusLabel", "recipientLabel"].includes(column.id)}
      <WorkOrderTableCell row={item.workOrderRow} {column} />
    {:else if column.id === "orderSummary"}
      <CancelReplanWorkOrderCell workOrder={item.workOrder} />
    {:else if column.id === "statusLabel"}
      {#if item.event}
        <CancelReplanAppointmentCell row={item} />
      {:else}
        —
      {/if}
    {:else if column.id === "propertyLabel"}
      <CancelReplanPropertyCell row={item} {unitContactContexts} />
    {:else if column.id === "recipientLabel"}
      <WorkOrderContactTags
        tags={item.workOrderRow?.recipientTags ?? []}
        stacked={true}
        splitRoleAndName={true}
      />
    {/if}
  {/snippet}
</Table>

<style lang="less">
  .loading-cell {
    width: min(100%, 11rem);
    padding: 0.25rem 0;

    &.wide {
      width: min(100%, 15rem);
    }
  }

  .loading-placeholder {
    display: block;
    width: 100%;
    height: 0.8rem;
    background: linear-gradient(
      90deg,
      color-mix(in srgb, var(--primary-color-100, #16c969) 10%, transparent),
      color-mix(in srgb, var(--primary-color-100, #16c969) 28%, transparent),
      color-mix(in srgb, var(--primary-color-100, #16c969) 10%, transparent)
    );
    background-size: 200% 100%;
    animation: cancel-target-loading-pulse 1.1s ease-in-out infinite;

    &.compact {
      width: 3rem;
    }
  }

  @keyframes cancel-target-loading-pulse {
    0% {
      background-position: 200% 0;
    }

    100% {
      background-position: -200% 0;
    }
  }
</style>
